// PASSWORD-MANAGER AUTOFILL ON THE FUNNEL (act:7112377e).
//
// A password manager fills a form by writing straight into the DOM node's
// `value` property. React's CONTROLLED inputs are hostile to that in two
// separate ways, and both end with the firm receiving nothing:
//
//   1. THE SWALLOW. React installs a value tracker on every controlled input
//      and compares against it before firing onChange. A manager that writes
//      the property and then dispatches a synthetic `input` event can land in
//      the case where the tracker already holds the new value, so onChange
//      never fires and React state stays "".
//   2. THE WIPE, which is the worse one. React re-applies `node.value` from
//      the `value` prop on EVERY commit of that element — not only when the
//      prop changed (react-dom 19.2.6, `updateInput`: it compares the node's
//      value to the prop's and overwrites the node). So a value React never
//      learned about is erased by the next unrelated re-render. On the sign-up
//      step the Turnstile token arriving is such a re-render; on the signing
//      page every acknowledgment tick is one. The claimant watches their
//      details appear and then vanish.
//
// THE FIX IS STRUCTURAL, NOT COMPENSATORY. Every autofillable input on the
// funnel is UNCONTROLLED — React is never given a `value` prop for it, so
// `updateInput` takes the defaultValue branch and never writes `node.value`.
// Nothing can be wiped, and nothing depends on a manager dispatching an event.
// The DOM is then read once, at submit, through this module.
//
// That is why there is no polling loop and no synthetic-event listener here.
// Both were designed and cut. Be precise about why, because the tempting
// shorthand is wrong: React still installs a value tracker on an uncontrolled
// input (`initInput` calls `track(element)` unconditionally, outside every
// conditional), so onChange can still be suppressed exactly as before. What
// changed is that it no longer MATTERS — the wipe is gone, so whatever a
// manager wrote is still sitting in the box at submit, and the only question
// left is "what is in the boxes when they press the button". One read answers
// that exactly, with no race to win.
//
// TWO STRUCTURAL ALLOWLISTS, both load-bearing on the signing page. `readAutofilled`
// iterates the FIELD LIST and asks the DOM for each name; it never enumerates a
// form's elements. `mergeAutofilled` only writes keys the caller already held.
// The reason is `#typed-signature`, which lives inside the same <form> as the six
// contact fields, carries an autocomplete token, and is rendered into the base64
// PNG the server freezes as evidence. A sweep over "every input in this form"
// would let a password manager author an electronic signature. It cannot happen
// through this module by construction, not by care.

// The three fields the sign-up step asks for. Mirrors the qualify POST's keys.
export const SIGNUP_AUTOFILL_FIELDS = ["first_name", "last_name", "email"]

// The six the signing page asks for, in render order. This is the JS twin of
// `Claimant::SIGNING_TIME_CONTACT_FIELDS` — the Ruby SSOT that also drives the
// column length bound and `FunnelController#signing_contact_attrs` — and
// spec/frontend/autofill_sync_spec.rb asserts the two are equal, element for
// element, in order. Do not add a key here without adding it there.
export const CONTACT_AUTOFILL_FIELDS = [
  "phone",
  "address_line1",
  "address_line2",
  "city",
  "state",
  "zip",
]

const US_PHONE_DIGITS = 10

// Digits only, with a leading US country code dropped when what remains is a
// whole 10-digit number.
function usPhoneDigits(raw) {
  const digits = String(raw == null ? "" : raw).replace(/\D/g, "")
  if (digits.length === US_PHONE_DIGITS + 1 && digits.startsWith("1")) {
    return digits.slice(1)
  }
  return digits
}

// NORMALIZE ONLY WHEN WE ARE CERTAIN; OTHERWISE PASS THROUGH UNTOUCHED.
//
// The phone input is masked `(000) 000-0000`. A manager that fills it silently
// leaves whatever it stored — commonly E.164 (`+15551234567`) or
// `+1 555-123-4567` — sitting in the node unmasked, because the mask only runs
// on input events it actually sees. Reformatting a confidently-recognized US
// number is a real improvement; guessing at anything else is how a law firm's
// client record acquires a wrong phone number with no error anywhere. So an
// extension, a 7-digit fragment, or a genuinely international number is handed
// to the server exactly as the claimant left it, and a human can read it.
export function normalizeUsPhone(raw) {
  const value = String(raw == null ? "" : raw).trim()
  if (value === "") return ""
  const digits = usPhoneDigits(value)
  if (digits.length !== US_PHONE_DIGITS) return value
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

// IMask's `prepare` hook — the OTHER half of the phone story, covering the path
// `normalizeUsPhone` cannot reach.
//
// When a manager DOES dispatch an event, IMask processes the fill before this
// module ever sees it, and `(000) 000-0000` fed `+15551234567` keeps the first
// ten digits: `(155) 512-3456`. That is a plausible-looking, completely wrong
// number, and by submit time it is indistinguishable from one someone typed.
// The corruption has to be prevented at the input, which is what this does —
// strip the country code out of a fill landing in an EMPTY field, before the
// mask can consume it. Mid-edit input is passed through untouched so ordinary
// typing is unaffected.
//
// It must be a stable module-level function: react-imask re-runs
// `updateOptions` on every re-render and only leaves the element alone when the
// options compare equal, so a fresh inline arrow would re-apply the mask (and
// wipe an unmasked fill) on every render.
//
// AND IT REFUSES WHAT THE MASK WOULD LIE ABOUT — the one place this function
// and normalizeUsPhone deliberately disagree. A fill that opens with `+` and is
// not a US number cannot be represented by `(000) 000-0000` at all; left alone,
// the mask keeps the first ten digits and `+44 20 7946 0958` becomes
// `(442) 079-4609` — a perfectly plausible North Carolina number that reaches
// nobody, and that by submit time is indistinguishable from one somebody typed.
// Returning nothing leaves the box empty, which the claimant can see and act on.
// An empty field is a question; a wrong number is an answer, and the wrong one.
//
// The asymmetry with normalizeUsPhone is deliberate and worth stating once: on
// the silent path nothing has mangled the value, so handing the raw string to
// the server preserves the truth and a human can read it. Here the mask is about
// to destroy it, there is nothing left to preserve, and refusing is the only way
// to avoid manufacturing a falsehood. A leading `+` is the only signal available
// that the number is not US — a bare digit run is ambiguous (`5551234567 x22`
// truncates to exactly the right number) and is passed through.
export function prepareUsPhoneInput(str, masked) {
  if (masked?.value) return str
  const value = String(str == null ? "" : str).trim()
  const digits = value.replace(/\D/g, "")
  if (digits.length === US_PHONE_DIGITS + 1 && digits.startsWith("1")) {
    return digits.slice(1)
  }
  if (value.startsWith("+") && digits.length !== US_PHONE_DIGITS) return ""
  return str
}

// Read the live DOM values for a FIXED list of field names. Blank and
// whitespace-only values are omitted, so an untouched field never reports
// itself as an answer.
//
// `root` is any element with `querySelector` (the <form>, or the contact
// panel). Missing root, missing field, and non-string values all read as
// absent rather than raising — this runs inside a submit handler on the page
// that captures a signature, and a throw here would cost the claimant their
// retainer.
export function readAutofilled(root, fields) {
  const found = {}
  if (!root || typeof root.querySelector !== "function") return found
  for (const field of fields) {
    let el = null
    try {
      el = root.querySelector(`[name="${field}"]`)
    } catch {
      el = null
    }
    if (!el) continue
    const value = typeof el.value === "string" ? el.value.trim() : ""
    if (value !== "") found[field] = value
  }
  return found
}

// Merge what the DOM holds over what the component held.
//
// NON-BLANK WINS, BLANK NEVER CLEARS. The DOM is authoritative for anything the
// user or their manager put on screen; but this exists to RECOVER values, never
// to erase them, so an empty node can't blank a value the component is holding.
// Keys absent from `held` are ignored — the second allowlist.
export function mergeAutofilled(held, found) {
  const next = { ...held }
  for (const key of Object.keys(next)) {
    const value = found[key]
    if (typeof value === "string" && value !== "" && value !== next[key]) {
      next[key] = value
    }
  }
  return next
}

export function pickFields(source, fields) {
  const picked = {}
  for (const field of fields) picked[field] = source?.[field] || ""
  return picked
}

// The sign-up step's three values as they will ride the qualify POST.
export function signupValues(formEl, held) {
  return mergeAutofilled(
    pickFields(held, SIGNUP_AUTOFILL_FIELDS),
    readAutofilled(formEl, SIGNUP_AUTOFILL_FIELDS),
  )
}

// The signing page's six contact values as they will ride the sign POST.
//
// `revealed` is the fail-closed gate: the block is collapsed by default, and a
// manager that fills a block the claimant never opened has produced values the
// claimant never saw. Writing those to the firm's record — on the screen where
// they are signing a retainer, over a phone number that may already be on file
// — is not something to do on a guess. Opening the disclosure once is the
// signal that they were offered; collapsing it again does NOT retract, because
// the toggle is labelled as a show/hide and hiding a thing is not deleting it.
//
// Only the phone is normalized. The five address fields are handed over as
// typed: the server strips them, bounds their length, and refuses to let a
// blank overwrite what is on file.
export function contactPayload(panelEl, { revealed }) {
  if (!revealed) return {}
  const found = readAutofilled(panelEl, CONTACT_AUTOFILL_FIELDS)
  if (found.phone) found.phone = normalizeUsPhone(found.phone)
  return found
}
